/**
 * 网校 WebSocket 连接工具
 * 学员登录后建立连接，用于消息上报与接收
 */

const DEFAULT_TERMINAL = 'web_h5'
const RECONNECT_INTERVAL = 3000
const MAX_RECONNECT_ATTEMPTS = 5

class WebSocketClient {
  constructor() {
    this.ws = null
    this.connected = false
    this.manualClose = false
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.userId = ''
    this.terminal = DEFAULT_TERMINAL
    this.listeners = {
      message: [],
      open: [],
      close: [],
      error: []
    }
  }

  /**
   * 构建 WebSocket 连接地址
   */
  buildUrl(userId, terminal) {
    const proto = 'wss'
    const host = process.env.VUE_APP_WS_HOST || 'websoket.shkt.online:18954'
    const uid = encodeURIComponent(userId || 'anonymous')
    const term = encodeURIComponent(terminal || DEFAULT_TERMINAL)
    return `${proto}://${host}/ws?user_id=${uid}&terminal=${term}`
  }

  /**
   * 是否已连接
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }

  /**
   * 建立连接
   * @param {Object} options
   * @param {string} options.userId - 学员 ID（fans_id）
   * @param {string} [options.terminal] - 终端标识，默认 web_h5
   * @param {string} [options.url] - 自定义 ws 地址，不传则使用默认地址
   */
  connect(options = {}) {
    const { userId, terminal = DEFAULT_TERMINAL, url } = options

    if (!userId) {
      console.warn('[WebSocket] 缺少 userId，无法连接')
      return
    }

    if (this.isConnected() && this.userId === String(userId)) {
      return
    }

    this.disconnect(false)
    this.userId = String(userId)
    this.terminal = terminal
    this.manualClose = false
    this.reconnectAttempts = 0

    const wsUrl = url || this.buildUrl(this.userId, this.terminal)
    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      this.connected = true
      this.reconnectAttempts = 0
      this._emit('open')
      console.log('WebSocket 连接成功')
    }

    this.ws.onclose = () => {
      this.connected = false
      this._emit('close')
      if (!this.manualClose) {
        this._scheduleReconnect()
      }
    }

    this.ws.onerror = (err) => {
      this._emit('error', err)
    }

    this.ws.onmessage = (event) => {
      let data = event.data
      try {
        data = JSON.parse(event.data)
      } catch (e) {
        // 非 JSON 消息保持原样
      }
      this._emit('message', data)
    }
  }

  /**
   * 从 localStorage 读取 fans_id 并连接（刷新页面后调用）
   */
  connectWithStoredUser(terminal) {
    const userId = localStorage.getItem('tengface_fans_id')
    if (!userId) {
      return false
    }
    this.connect({ userId, terminal })
    return true
  }

  /**
   * 断开连接
   * @param {boolean} manual - 是否手动断开（手动断开时不自动重连）
   */
  disconnect(manual = true) {
    this.manualClose = manual
    this._clearReconnectTimer()
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
      this.ws = null
    }
    this.connected = false
  }

  /**
   * 发送消息
   * @param {string|Object} content - 消息内容，字符串或对象
   * @param {Object} [extra] - 附加字段，会合并进 payload
   */
  send(content, extra = {}) {
    if (!this.isConnected()) {
      console.warn('[WebSocket] 未连接，消息发送失败')
      return false
    }

    const payload = {
      user_id: this.userId,
      terminal: this.terminal,
      ...extra
    }

    if (typeof content === 'string') {
      payload.content = content.trim()
      if (!payload.content) return false
    } else if (content && typeof content === 'object') {
      Object.assign(payload, content)
    } else {
      return false
    }

    this.ws.send(JSON.stringify(payload))
    return true
  }

  /**
   * 监听事件：open | close | error | message
   */
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback)
    }
    return () => this.off(event, callback)
  }

  off(event, callback) {
    if (!this.listeners[event]) return
    this.listeners[event] = this.listeners[event].filter((fn) => fn !== callback)
  }

  _emit(event, data) {
    this.listeners[event].forEach((fn) => {
      try {
        fn(data)
      } catch (e) {
        console.error('[WebSocket] 事件回调异常:', e)
      }
    })
  }

  _scheduleReconnect() {
    if (this.manualClose || !this.userId) return
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[WebSocket] 已达最大重连次数')
      return
    }
    this._clearReconnectTimer()
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts += 1
      this.connect({ userId: this.userId, terminal: this.terminal })
    }, RECONNECT_INTERVAL)
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}

export default new WebSocketClient()
